import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Grid, Card, CardContent
} from '@mui/material';
import { metadataService } from '../services/metadataService';

const DIMENSION_LABELS = {
  temporal: 'Temporal',
  geographical: 'Geographical',
  technological: 'Technological',
  reliability: 'Reliability',
  coverage: 'Coverage',
};

const DIMENSION_WEIGHTS = {
  temporal: 0.25,
  geographical: 0.20,
  technological: 0.25,
  reliability: 0.20,
  coverage: 0.10,
};

function QualityMetadata() {
  const [entityType, setEntityType] = useState('embedded_emission');
  const [entityId, setEntityId] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lowQuality, setLowQuality] = useState(null);

  const handleLookup = async () => {
    if (!entityId.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const data = await metadataService.getMetadata(entityType, entityId);
      setMetadata(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setMetadata(null);
        setError('No metadata found for this entity');
      } else {
        setError(err.response?.data?.error?.message || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFindLowQuality = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await metadataService.findBelowThreshold(entityType, 0.5);
      setLowQuality(data ?? {});
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Map PACT DQI score (1.0=best, 3.0=worst) to a 0-1 quality percentage
  const dqrToPercent = (dqr) => {
    if (dqr === null || dqr === undefined) return 0;
    // 1.0 -> 100%, 2.0 -> 50%, 3.0 -> 0%
    return Math.max(0, Math.min(1, (3 - parseFloat(dqr)) / 2));
  };

  // Extract dimension scores from PACT DQI backend response
  const getDimensionScore = (key) => {
    if (!metadata) return 0;
    if (key === 'coverage') {
      return (parseFloat(metadata.coverage_percent) || 0) / 100;
    }
    const fieldMap = {
      temporal: 'temporal_dqr',
      geographical: 'geographical_dqr',
      technological: 'technological_dqr',
      reliability: 'reliability_dqr',
    };
    return dqrToPercent(metadata[fieldMap[key]]);
  };

  const getAggregateScore = () => {
    if (!metadata) return 0;
    // total_score is 0-100 from backend
    if (metadata.total_score !== undefined) return parseFloat(metadata.total_score) / 100;
    return 0;
  };

  const qualityColor = (score) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.5) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Active Metadata & Quality Scores</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        5-dimension quality scoring with weighted aggregate and immutable data lineage
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <TextField fullWidth label="Entity Type" value={entityType} onChange={(e) => setEntityType(e.target.value)} select SelectProps={{ native: true }}>
              <option value="embedded_emission">Embedded Emission</option>
              <option value="direct_emission">Direct Emission</option>
              <option value="e_ledger">E-Ledger</option>
            </TextField>
          </Grid>
          <Grid item xs={5}>
            <TextField fullWidth label="Entity ID (UUID)" value={entityId} onChange={(e) => setEntityId(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleLookup()} />
          </Grid>
          <Grid item xs={2}>
            <Button variant="contained" fullWidth onClick={handleLookup} disabled={loading} sx={{ height: 56 }}>
              {loading ? <CircularProgress size={24} /> : 'Lookup'}
            </Button>
          </Grid>
          <Grid item xs={2}>
            <Button variant="outlined" fullWidth onClick={handleFindLowQuality} disabled={loading} sx={{ height: 56 }}>
              Low Quality
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {metadata && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quality Score: <Chip label={`${(getAggregateScore() * 100).toFixed(1)}%`} color={qualityColor(getAggregateScore())} />
              </Typography>

              <Grid container spacing={2}>
                {Object.entries(DIMENSION_LABELS).map(([key, label]) => {
                  const score = getDimensionScore(key);
                  return (
                    <Grid item xs={2.4} key={key}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" color={qualityColor(score) + '.main'}>{(score * 100).toFixed(0)}%</Typography>
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="caption" color="text.secondary">weight: {DIMENSION_WEIGHTS[key]}</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {(metadata?.data_lineage?.length ?? 0) > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Data Lineage ({(metadata?.data_lineage?.length ?? 0)} stages)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(metadata?.data_lineage ?? []).map((entry, i) => (
                    <TableRow key={i}>
                      <TableCell>{entry.timestamp || '-'}</TableCell>
                      <TableCell>{entry.stage || entry.action || '-'}</TableCell>
                      <TableCell>{entry.source || entry.actor || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {lowQuality && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Quality Summary for {entityType}</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{lowQuality.total_records || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Total Records</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color={qualityColor((parseFloat(lowQuality.avg_score) || 0) / 100) + '.main'}>
                  {(parseFloat(lowQuality.avg_score) || 0).toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Avg Score</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{(parseFloat(lowQuality.min_score) || 0).toFixed(1)}%</Typography>
                <Typography variant="body2" color="text.secondary">Min Score</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{(parseFloat(lowQuality.max_score) || 0).toFixed(1)}%</Typography>
                <Typography variant="body2" color="text.secondary">Max Score</Typography>
              </Box>
            </Grid>
          </Grid>
          {(lowQuality?.distribution?.length ?? 0) > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Verification Type</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(lowQuality?.distribution ?? []).map((d, i) => (
                  <TableRow key={i}>
                    <TableCell>{d.type}</TableCell>
                    <TableCell align="right">{d.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {(!lowQuality?.distribution || (lowQuality?.distribution?.length ?? 0) === 0) && (
            <Typography variant="body2" color="text.secondary" align="center">No quality records found</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default QualityMetadata;
